import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import useEmblaCarousel from "embla-carousel-react";
import { useCart } from "@/context/CartContext";
import CartDrawer from "@/components/CartDrawer";
import CheckoutDialog from "@/components/CheckoutDialog";
import ProductDetailSheet from "@/components/ProductDetailSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBanner = ({ status }) => {
  if (!status) return null;
  const map = {
    paid: "PAYMENT SUCCESSFUL — ORDER CONFIRMED",
    pending: "PROCESSING PAYMENT…",
    error: "PAYMENT ERROR — TRY AGAIN",
    cancelled: "CHECKOUT CANCELLED",
    expired: "SESSION EXPIRED",
  };
  return (
    <div
      className="w-full bg-black text-white py-3 text-center text-xs font-bold tracking-[0.3em] uppercase"
      data-testid="payment-status-banner"
    >
      {map[status] || map.pending}
    </div>
  );
};

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [statusBanner, setStatusBanner] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [autoplayActive, setAutoplayActive] = useState(true);
  const autoplayRef = useRef(null);
  const { itemCount, setOpen, clear } = useCart();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    dragFree: false,
    skipSnaps: false,
  });

  const scrollTo = useCallback(
    (i) => {
      if (!emblaApi) return;
      setAutoplayActive(false);
      emblaApi.scrollTo(i);
    },
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Auto-shuffle on landing; stop on first user interaction (touch/drag/click)
  useEffect(() => {
    if (!emblaApi || !autoplayActive) return;
    autoplayRef.current = setInterval(() => {
      emblaApi.scrollNext();
    }, 2200);
    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, [emblaApi, autoplayActive]);

  useEffect(() => {
    if (!emblaApi) return;
    const stop = () => setAutoplayActive(false);
    emblaApi.on("pointerDown", stop);
    return () => emblaApi.off("pointerDown", stop);
  }, [emblaApi]);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/products`)
      .then((res) => mounted && setProducts(res.data))
      .catch((e) => console.error("Failed to load products", e))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Stripe return: poll for status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");

    if (cancelled) {
      setStatusBanner("cancelled");
      window.history.replaceState({}, "", "/");
      return;
    }
    if (!sessionId) return;

    setStatusBanner("pending");
    let attempts = 0;
    const maxAttempts = 7;
    const poll = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const ps = res.data.payment_status;
        const st = res.data.status;
        if (ps === "paid") {
          setStatusBanner("paid");
          clear();
          window.history.replaceState({}, "", "/");
          return;
        }
        if (st === "expired") {
          setStatusBanner("expired");
          window.history.replaceState({}, "", "/");
          return;
        }
        if (attempts >= maxAttempts) {
          setStatusBanner("error");
          window.history.replaceState({}, "", "/");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        console.error(e);
        if (attempts >= maxAttempts) {
          setStatusBanner("error");
          window.history.replaceState({}, "", "/");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [clear]);

  const openDetail = (product) => {
    setDetailProduct(product);
    setDetailOpen(true);
  };

  return (
    <div className="min-h-screen w-full bg-white text-black flex flex-col">
      <StatusBanner status={statusBanner} />

      {/* Top nav (cart only) */}
      <nav
        className="w-full flex items-center justify-between px-6 md:px-12 py-6"
        data-testid="top-nav"
      >
        <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-black/40">
          Shop
        </span>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[11px] font-bold tracking-[0.3em] uppercase hover:opacity-60 transition-opacity"
          data-testid="open-cart-btn"
        >
          Cart [{itemCount}]
        </button>
      </nav>

      {/* Brand header */}
      <header className="w-full px-6 md:px-12 pt-8 md:pt-16 pb-8 md:pb-12 flex flex-col items-center">
        <h1
          className="meao-wordmark text-[28vw] md:text-[22vw] lg:text-[18rem] text-black select-none"
          data-testid="brand-header"
          aria-label="MEAO"
        >
          MEAO
        </h1>
        <p
          className="mt-3 text-sm md:text-base font-medium tracking-[0.2em] text-black/40 lowercase"
          data-testid="brand-tagline"
        >
          my eyes are open
        </p>
      </header>

      {/* Carousel */}
      <main
        className="w-full flex-1 px-0 md:px-12 pb-16 md:pb-24"
        data-testid="shop-carousel-section"
      >
        {loading ? (
          <div className="text-center py-32 text-sm tracking-[0.3em] uppercase text-black/40">
            Loading…
          </div>
        ) : (
          <div className="relative w-full max-w-[1600px] mx-auto">
            <div
              className="overflow-hidden"
              ref={emblaRef}
              data-testid="shop-carousel"
            >
              <div className="flex">
                {products.map((p, i) => (
                  <div
                    key={p.id}
                    className="flex-[0_0_100%] md:flex-[0_0_70%] lg:flex-[0_0_55%] min-w-0 px-6 md:px-8 flex justify-center"
                  >
                    <button
                      type="button"
                      onClick={() => openDetail(p)}
                      className="group flex flex-col items-center w-full max-w-[640px] outline-none"
                      data-testid={`product-card-${p.id}`}
                      aria-label={`Open ${p.name}`}
                    >
                      <div className="w-full aspect-square flex items-center justify-center">
                        <img
                          src={p.image}
                          alt={p.name}
                          className={`w-full max-w-[560px] aspect-square object-contain transition-all duration-700 ease-out select-none ${
                            p.id === "crewneck" ? "scale-125 mix-blend-multiply" : ""
                          } ${
                            p.id === "hoodie" ? "scale-125" : ""
                          } ${
                            selectedIndex === i
                              ? "opacity-100"
                              : "opacity-40 !scale-90"
                          } group-hover:opacity-100`}
                          data-testid={`product-img-${p.id}`}
                          draggable={false}
                        />
                      </div>
                      <div
                        className={`mt-6 flex flex-col items-center gap-1 transition-opacity duration-500 ${
                          selectedIndex === i ? "opacity-100" : "opacity-40"
                        }`}
                      >
                        <h3
                          className="text-xl md:text-2xl font-black tracking-tight uppercase"
                          data-testid={`product-name-${p.id}`}
                        >
                          {p.name}
                        </h3>
                        <p
                          className="text-sm font-medium text-black/60 tracking-wide"
                          data-testid={`product-price-${p.id}`}
                        >
                          ${p.price.toFixed(2)}
                        </p>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Dots */}
            <div
              className="flex justify-center gap-3 mt-10"
              data-testid="carousel-dots"
            >
              {products.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => scrollTo(i)}
                  className={`h-[2px] transition-all duration-300 ${
                    selectedIndex === i ? "w-12 bg-black" : "w-6 bg-black/20"
                  }`}
                  data-testid={`carousel-dot-${i}`}
                  aria-label={`Go to product ${i + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="w-full border-t border-black/10 px-6 md:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-2">
        <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-black/40">
          © MEAO {new Date().getFullYear()}
        </span>
        <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-black/30">
          All sales final
        </span>
      </footer>

      <ProductDetailSheet
        product={detailProduct}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
      <CartDrawer onCheckout={() => setCheckoutOpen(true)} />
      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </div>
  );
}
